import { HoldingsQuote } from '@benzinga/quotes-v3-holdings-manager';
import { Quote } from '@benzinga/quotes-v3-manager';
import { DEFAULT_SCANNER_CONFIG, ScannerConfig } from '@benzinga/scanner-config-manager';
import { ScannerManager } from '@benzinga/scanner-manager';
import { SessionContext } from '@benzinga/session-context';
import React from 'react';

export const useBatchQuotes = (
  symbols: string[],
  fields: Set<keyof Quote>,
  config?: Partial<ScannerConfig>,
  refreshKey: number | string = 0,
) => {
  const [quotes, setQuotes] = React.useState<HoldingsQuote[] | undefined>(undefined);
  const [hasInitialQuotesLoaded, setHasInitialQuotesLoaded] = React.useState(false);

  const symbolsKey = React.useMemo(() => JSON.stringify([...symbols].sort()), [symbols]);

  React.useEffect(() => {
    setQuotes(undefined);
    setHasInitialQuotesLoaded(false);
  }, [symbolsKey]);

  const handleQuotes = React.useCallback((quotes: HoldingsQuote[]) => {
    setQuotes(quotes);
    setHasInitialQuotesLoaded(true);
  }, []);

  useBatchQuotesCallback(symbols, fields, handleQuotes, hasInitialQuotesLoaded, config, refreshKey);

  return quotes;
};

export const useBatchQuotesCallback = (
  symbols: string[],
  fields: Set<keyof Quote>,
  callback: (quotes: HoldingsQuote[]) => void,
  hasInitialQuotesLoaded = false,
  config?: Partial<ScannerConfig>,
  refreshKey: number | string = 0,
) => {
  const session = React.useContext(SessionContext);
  const refreshMs = (config?.refreshInterval ?? 0) * 1000;
  const lastFetchTimeRef = React.useRef<number>(0);
  const lastConfigKey = React.useRef<string>('');
  const lastRefreshKeyRef = React.useRef(refreshKey);
  const limit = 700;

  const fetchQuotes = React.useCallback(
    async (startIndex, endIndex) => {
      const quotes = await session.getManager(ScannerManager).getInstruments({
        ...DEFAULT_SCANNER_CONFIG,
        filters: [
          {
            field: 'symbol',
            operator: 'in',
            parameters: symbols?.slice(startIndex, endIndex) ?? [],
          },
        ],
        limit,
        tableParameters: {
          ...DEFAULT_SCANNER_CONFIG.tableParameters,
          columns: [...Array.from(fields)].map(c => ({ colId: c })),
        },
        ...config,
      });

      if (quotes.err) return;
      return quotes.ok.instruments;
    },
    [session, symbols, fields, config],
  );

  const InitialQuoteFetch = React.useCallback(
    async (force = false) => {
      if (!symbols?.length || fields.size === 0) return;

      const configKey = JSON.stringify({
        config,
        fields: Array.from(fields),
        symbols,
      });

      const now = Date.now();
      const isSameInputs = lastConfigKey.current === configKey;

      if (!force && isSameInputs && refreshMs > 0 && now - lastFetchTimeRef.current < refreshMs) {
        return;
      }
      lastConfigKey.current = configKey;

      const batchedQuotes: HoldingsQuote[] = [];
      const results = await Promise.allSettled(
        Array.from({ length: Math.ceil(symbols.length / limit) }, (_, idx) =>
          fetchQuotes(idx * limit, (idx + 1) * limit),
        ),
      );
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value) {
          batchedQuotes.push(...(result.value as HoldingsQuote[]));
        }
      });
      if (batchedQuotes.length > 0) {
        lastFetchTimeRef.current = Date.now();
        callback(batchedQuotes);
      }
    },
    [symbols, config, fields, refreshMs, fetchQuotes, callback],
  );
  React.useEffect(() => {
    if (!hasInitialQuotesLoaded) {
      InitialQuoteFetch();
    }
  }, [InitialQuoteFetch, hasInitialQuotesLoaded]);

  React.useEffect(() => {
    if (refreshKey === 0 || refreshKey === lastRefreshKeyRef.current) return;
    lastRefreshKeyRef.current = refreshKey;
    InitialQuoteFetch(true);
  }, [refreshKey, InitialQuoteFetch]);
  React.useEffect(() => {
    if (!hasInitialQuotesLoaded || !config?.refreshInterval) return;
    InitialQuoteFetch();
    const interval = setInterval(() => {
      InitialQuoteFetch();
    }, config.refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [hasInitialQuotesLoaded, config?.refreshInterval, InitialQuoteFetch]);
};
